import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { dataverseScopes } from "../auth/msalConfig";
import { fetchInspectionsDirect } from "../api/dataverse";
import { Inspection } from "../api/inspectionApi";

export const LandInspectionPage: React.FC = () => {
  const { instance } = useMsal();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [municipality, setMunicipality] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    instance
      .acquireTokenSilent({
        scopes: dataverseScopes,
        account: instance.getActiveAccount() || undefined,
      })
      .then((r) => fetchInspectionsDirect(r.accessToken, municipality || undefined))
      .then(setInspections)
      .catch(() => setError("No se pudo cargar las inspecciones."))
      .finally(() => setLoading(false));
  }, [municipality, instance]);

  const statusBadge = (status: string) => {
    const cls =
      status === "Completada"  ? "bg-green-100 text-green-700" :
      status === "Programada"  ? "bg-blue-100 text-blue-700"   :
      status === "En Progreso" ? "bg-amber-100 text-amber-700" :
      status === "Cancelada"   ? "bg-red-100 text-red-700"     :
                                 "bg-gray-100 text-gray-500";
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
        {status}
      </span>
    );
  };

  const resultBadge = (result: string) => {
    const cls =
      result === "Aprobada"  ? "bg-green-100 text-green-700" :
      result === "Rechazada" ? "bg-red-100 text-red-700"     :
      result === "Pendiente" ? "bg-amber-100 text-amber-700" :
                               "bg-gray-100 text-gray-500";
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
        {result}
      </span>
    );
  };

  const fmt = (d: string | null) =>
    d ? new Date(d).toLocaleDateString("es-PR") : "—";

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-800 mb-4">Inspecciones</h1>

      {/* Filter bar */}
      <div className="flex gap-3 mb-6">
        <input
          type="text"
          placeholder="Filtrar por municipio..."
          value={municipality}
          onChange={(e) => setMunicipality(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading && (
        <p className="text-gray-500 text-sm">Cargando inspecciones...</p>
      )}

      {error && (
        <p className="text-red-600 text-sm">{error}</p>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["ID Inspección","Subparcela","Municipio","F. Programada","F. Completada","Inspector","Estado","Resultado","Seguimiento"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {inspections.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-gray-400">
                    No se encontraron inspecciones.
                  </td>
                </tr>
              ) : (
                inspections.map((ins) => (
                  <tr key={ins.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{ins.inspectionId}</td>
                    <td className="px-4 py-3 text-gray-700">{ins.subParcel}</td>
                    <td className="px-4 py-3 text-gray-700">{ins.municipality}</td>
                    <td className="px-4 py-3 text-gray-500">{fmt(ins.scheduledDate)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmt(ins.completedDate)}</td>
                    <td className="px-4 py-3 text-gray-700">{ins.inspector}</td>
                    <td className="px-4 py-3">{statusBadge(ins.status)}</td>
                    <td className="px-4 py-3">{resultBadge(ins.overallResult)}</td>
                    <td className="px-4 py-3 text-center">
                      {ins.followUpRequired ? (
                        <span className="text-amber-600 font-medium text-xs">Sí</span>
                      ) : (
                        <span className="text-gray-400 text-xs">No</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
